/* DEAL-5 - the Golf At Home price test ($119 today / $149 arm B / $179 arm C).
   Keeps every tagged price surface in step with the arm class at all times.

   Liquid stamps EVERY arm's finished string on each surface as inert
   data-gd-deal-a / data-gd-deal-b / data-gd-deal-c attributes (composed in
   Liquid, so no money formatting happens here), and this follower copies the
   right one into textContent. With the flags off nothing is ever written, so the rendered
   page stays byte-identical to today. It must survive the cart drawer
   wholesale re-stamping its cards from SSR control HTML on every cart
   mutation (syncAdditionalProducts / renderContents), Intelligems landing
   2.5-5 SECONDS after first paint, and bfcache restoring stale text under a
   live class - hence: idempotent, observer-driven, arm read at call time
   rather than captured, and armed only while a test is actually running.
   Separate file so the whole apparatus deletes cleanly when the test
   concludes. Namespaced away from GAH-DELTA-01 (data-gd-a / gd-delta-b /
   GD_DELTA) on purpose: the two tests can run over each other, and a node must
   never carry both tests' attributes or the two followers fight over its text.

   Contract for the surfaces: stamp BOTH the -a and -b attributes on the same
   element, and nothing else inside it (this writes textContent, so any child
   markup in a tagged node is destroyed the first time an arm renders). A node
   carrying only one of the two is ignored - to say "arm B hides this", stamp
   data-gd-deal-b="" rather than leaving the attribute off. data-gd-deal-c is
   OPTIONAL and rides alongside them; the a+b pair stays the entry ticket so
   that adding tier C changed no existing node's eligibility.

   Tier fallback: a node with no -c stamp renders its -b string while tier C
   is live (and, since -b may itself be "", inherits arm B's hide). Chosen
   over falling back to -a because tier C is defined as one step ABOVE tier B,
   so B's string is the nearest-neighbour value and the smaller visible error,
   and because a surface arm B deliberately hides must not reappear one tier
   higher. It is a stamping-gap backstop, not a supported state: a surface
   that reaches tier C without a -c stamp is a bug in that surface's Liquid. */

(() => {
  const armB = () => document.documentElement.classList.contains('gd-deal-b');
  const armC = () => document.documentElement.classList.contains('gd-deal-c');

  const isShown = (el) => {
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({ visibilityProperty: true });
    }
    const rect = el.getBoundingClientRect();
    return !!(rect.width || rect.height) && getComputedStyle(el).visibility !== 'hidden';
  };

  let observerArmed = false;
  const armObserver = () => {
    if (observerArmed) return;
    observerArmed = true;
    let queued = null;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = setTimeout(() => {
        queued = null;
        applyDealPrices();
      }, 60);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    // opening the drawer flips a class, not childList - without this a
    // drawer-only exposure (cart icon click, no cart mutation) never counts
    const drawer = document.querySelector('cart-drawer');
    if (drawer) observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  };

  const applyDealPrices = () => {
    const nodes = document.querySelectorAll('[data-gd-deal-a][data-gd-deal-b]');
    if (!nodes.length) return;
    // read at call time, never captured - the arm can flip seconds after load
    const useC = armC();
    // C wins if both classes somehow co-exist, matching apply()'s precedence.
    // apply() computes the two toggles together so that cannot happen, but a
    // follower that picked the lower tier here would show one price while the
    // click-time swap posted another.
    const useB = !useC && armB();
    nodes.forEach((node) => {
      let target;
      if (useC) {
        // no -c stamp on this node: fall back to its -b string (see the tier
        // fallback note at the top), which may itself be "" and hide it
        target = node.dataset.gdDealC !== undefined ? node.dataset.gdDealC : node.dataset.gdDealB;
      } else if (useB) {
        target = node.dataset.gdDealB;
      } else {
        target = node.dataset.gdDealA;
      }
      // an empty arm string means "this surface has no equivalent in the
      // priced arm": hide the node instead of rendering a blank price.
      // Reversible - the control pass below un-hides, so a class that flips
      // any which way lands correct, and only nodes THIS file hid are ever
      // restored.
      if ((useC || useB) && target === '') {
        if (!node.hasAttribute('data-gd-deal-hidden')) {
          node.setAttribute('data-gd-deal-hidden', '');
          node.style.display = 'none';
        }
        return;
      }
      if (node.hasAttribute('data-gd-deal-hidden')) {
        node.removeAttribute('data-gd-deal-hidden');
        node.style.display = '';
      }
      if (target && node.textContent !== target) node.textContent = target;
    });

    const flags = window.GD_FLAGS || {};
    if (flags.dealTest !== true) return;
    armObserver();
    // Exposure for pages the theme.liquid template gate can't see (the drawer
    // card renders sitewide): count the visitor once a tagged surface is
    // actually visible, not merely present in a closed drawer.
    if (!flags.armDeal && flags.exposeDealArm) {
      for (const node of nodes) {
        if (isShown(node)) {
          flags.exposeDealArm(true);
          break;
        }
      }
    }
  };

  window.GD_FLAGS = window.GD_FLAGS || {};
  window.GD_FLAGS.applyDealPrices = applyDealPrices;

  // bfcache: mobile Safari restores the frozen DOM - stale text, live class
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) applyDealPrices();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => applyDealPrices());
  } else {
    applyDealPrices();
  }
})();
